import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import useVendorStore from "../HomePage/useVendorStore";
import useCommonStore from "../Common/useCommonStore";
import {
    Button,
    FlexColumn,
    SushiModal,
    WhiteWrapper,
    Wrapper,
} from "../Common/styles.common";
import DDPoHeader from "../Header/DDPoHeader";
import { Loader } from "../Common/Loader";
import { STATUS_BEGIN, STATUS_DONE, PROFILE_INPUT_BORDER, MIME_TYPE_IMAGE_JPEG, MIME_TYPE_IMAGE_JPG, MIME_TYPE_IMAGE_PNG, MIME_TYPE_PDF } from "utils/constants/others";
import { PDFDocument } from "pdf-lib";
import styled from "styled-components";
import ShowToast from "../Common/Toast";
import WhiltelistedFileTypes from "../Common/Logout";

const SectionTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  margin: 24px 16px 16px 16px;
  color: #1c1c1c;
`;

const ContentContainer = styled.div`
  padding: 0 0 120px 0;
  overflow-y: auto;
  height: calc(100vh - 180px);
  background: #f8f8fb;
`;

const FormCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin: 0 16px 24px 16px;
  box-shadow: 0px 4px 20px rgba(0, 0, 0, 0.04);
  border: 1px solid #F2F4F7;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const UploadGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
`;

const UploadBox = styled.div`
  border: 1px dashed #ef4f5f;
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: #fff5f6;
  min-height: 110px;
  transition: all 0.2s ease;

  &:hover {
    background: #ffedef;
  }

  img {
    margin-bottom: 8px;
  }

  span {
    font-size: 13px;
    color: #ef4f5f;
    font-weight: 600;
    text-align: center;
  }
`;

const PreviewBox = styled.div`
  border: 1px solid #ebebeb;
  border-radius: 12px;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  width: 100%;

  .info {
    display: flex;
    align-items: center;
    gap: 12px;
    font-size: 14px;
    color: #4b5563;
    flex: 1;
    min-width: 0;
  }

  .remove {
    color: #ef4f5f;
    cursor: pointer;
    font-size: 18px;
    padding: 4px 8px;
    margin-left: 8px;
  }
`;

const UploadSupportingDocs = () => {
    const { purchaseOrderId } = useParams();
    const navigate = useNavigate();

    const getPurchaseOrders = useVendorStore((state) => state.getPurchaseOrders);
    const getPurchaseOrderStatus = useVendorStore((state) => state.getPurchaseOrderStatus);
    const purchaseOrders = useVendorStore((state) => state.purchaseOrders);
    const uploadSupportingDocs = useVendorStore((state) => state.uploadSupportingDocs);
    const uploadSupportingDocsStatus = useVendorStore((state) => state.uploadSupportingDocsStatus);
    const uploadDocs = useCommonStore((state) => state.uploadDocs);

    const [po, setPo] = useState(null);
    const [items, setItems] = useState([]);
    const [tonnage, setTonnage] = useState(0);
    const [showLoader, setShowLoader] = useState(false);
    const [toastMsg, setToastMsg] = useState("");
    const [showToast, setShowToast] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);

    const [files, setFiles] = useState({
        lorry_receipt: null,
        weight_slip: null,
        po_quality_certificate: null,
    });

    const [urls, setUrls] = useState({
        lorry_receipt: "",
        weight_slip: "",
        po_quality_certificate: "",
    });

    const [previews, setPreviews] = useState({
        lorry_receipt: null,
        weight_slip: null,
        po_quality_certificate: null,
    });

    useEffect(() => {
        getPurchaseOrders({
            is_item_required: true,
            purchase_order_id: purchaseOrderId,
        }, true);
    }, [purchaseOrderId]);

    useEffect(() => {
        if (getPurchaseOrderStatus === STATUS_DONE && purchaseOrders && purchaseOrders.length > 0) {
            const currentPo = purchaseOrders[0];
            setPo(currentPo);
            if (currentPo.purchase_order_items) {
                setItems(currentPo.purchase_order_items);
                let total = 0;
                for (const item of currentPo.purchase_order_items) {
                    total = total + item.quantity_ordered * (item.weight_per_packet || 0);
                }
                setTonnage(total);
            }
        }
    }, [purchaseOrders, getPurchaseOrderStatus]);

    const convertImageToPDF = async (imageFile) => {
        try {
            const pdfDoc = await PDFDocument.create();
            const imageBytes = await imageFile.arrayBuffer();
            let image;

            if (imageFile.type === MIME_TYPE_IMAGE_JPEG || imageFile.type === MIME_TYPE_IMAGE_JPG) {
                image = await pdfDoc.embedJpg(imageBytes);
            } else if (imageFile.type === MIME_TYPE_IMAGE_PNG) {
                image = await pdfDoc.embedPng(imageBytes);
            } else {
                return imageFile; // Return original if not supported image type
            }

            const page = pdfDoc.addPage([image.width, image.height]);
            page.drawImage(image, {
                x: 0,
                y: 0,
                width: image.width,
                height: image.height,
            });

            const pdfBytes = await pdfDoc.save();
            const pdfBlob = new Blob([pdfBytes], { type: MIME_TYPE_PDF });
            return new File([pdfBlob], imageFile.name.split('.')[0] + ".pdf", {
                type: MIME_TYPE_PDF,
            });
        } catch (error) {
            console.error("PDF conversion failed:", error);
            return imageFile;
        }
    };

    const handleFileSelect = async (field, file) => {
        if (file) {
            const isImage = WhiltelistedFileTypes.WhiltelistedFileTypes(file.type);
            const isPDF = WhiltelistedFileTypes.WhiltelistedFileTypesForDocuments(file.type);

            if (!isImage && !isPDF) {
                setToastMsg("Please upload only supported file types (.jpg, .jpeg, .png, .pdf)");
                setShowToast(true);
                return;
            }

            let fileToUpload = file;
            if (isImage) {
                // Show preview of the original image
                const reader = new FileReader();
                reader.onload = (e) => {
                    setPreviews(prev => ({ ...prev, [field]: e.target.result }));
                };
                reader.readAsDataURL(file);

                // Convert to PDF before uploading
                setShowLoader(true);
                fileToUpload = await convertImageToPDF(file);
                setShowLoader(false);
            }

            setFiles(prev => ({ ...prev, [field]: fileToUpload }));
            uploadFile(field, fileToUpload);
        }
    };

    const uploadFile = (field, file) => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append("fileType", field);

        setShowLoader(true);
        uploadDocs(formData, (url) => {
            setUrls(prev => ({ ...prev, [field]: url }));
            setShowLoader(false);
        });
    };

    const removeFile = (field) => {
        setFiles(prev => ({ ...prev, [field]: null }));
        setUrls(prev => ({ ...prev, [field]: "" }));
        setPreviews(prev => ({ ...prev, [field]: null }));
    };

    const getPathFromUrl = (url) => {
        if (!url) return "";
        try {
            const urlObj = new URL(url);
            return urlObj.pathname;
        } catch (e) {
            return url;
        }
    }

    const handleSubmit = () => {
        if (!urls.lorry_receipt && !urls.weight_slip && !urls.po_quality_certificate) {
            setToastMsg("Please upload at least one supporting document");
            setShowToast(true);
            return;
        }

        const payload = {
            purchase_order_id: parseInt(purchaseOrderId),
            lorry_receipt: getPathFromUrl(urls.lorry_receipt),
            quality_certificate: getPathFromUrl(urls.po_quality_certificate),
            weight_slip_path: getPathFromUrl(urls.weight_slip),
        };

        uploadSupportingDocs(payload, () => {
            setShowSuccessPopup(true);
        }, (err) => {
            setToastMsg(err || "Failed to upload supporting documents");
            setShowToast(true);
        });
    };

    const handleSuccessPopupClose = () => {
        setShowSuccessPopup(false);
        navigate(`/ddPo/details/source/${purchaseOrderId}`);
    }

    const UploadItem = ({ title, field }) => (
        <>
            <SectionTitle>{title}</SectionTitle>
            <FormCard>
                {!files[field] ? (
                    <UploadGrid>
                        <UploadBox onClick={() => document.getElementById(`${field}_img`).click()}>
                            <input
                                type="file"
                                id={`${field}_img`}
                                hidden
                                accept="image/*"
                                onChange={(e) => handleFileSelect(field, e.target.files[0])}
                            />
                            <img src="/logos/upload_image.svg" height="32" width="32" alt="Upload Image" />
                            <span>Upload image</span>
                        </UploadBox>
                        <UploadBox onClick={() => document.getElementById(`${field}_pdf`).click()}>
                            <input
                                type="file"
                                id={`${field}_pdf`}
                                hidden
                                accept="application/pdf"
                                onChange={(e) => handleFileSelect(field, e.target.files[0])}
                            />
                            <img src="/logos/upload_doc.svg" height="32" width="32" alt="Upload PDF" />
                            <span>Upload PDF</span>
                        </UploadBox>
                    </UploadGrid>
                ) : (
                    <PreviewBox>
                        <div className="info">
                            {previews[field] ? (
                                <img src={previews[field]} height="44" width="44" alt="Preview" style={{ borderRadius: '8px', objectFit: 'cover', border: '1px solid #eee' }} />
                            ) : (
                                <img src="/logos/pdf.svg" height="32" width="32" alt="File" />
                            )}
                            <span style={{ maxWidth: '180px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: '500' }}>
                                {files[field].name}
                            </span>
                            {urls[field] && <span style={{ color: '#10b981', fontWeight: 'bold' }}>✓</span>}
                        </div>
                        <div className="remove" onClick={() => removeFile(field)}>✕</div>
                    </PreviewBox>
                )}
            </FormCard>
        </>
    );

    return (
        <Wrapper>
            {(showLoader || uploadSupportingDocsStatus === STATUS_BEGIN) && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 9999
                }}>
                    <Loader />
                </div>
            )}
            {po && (
                <>
                    <DDPoHeader
                        poId={po.id}
                        purchaseOrderNumber={po.purchase_order_number}
                        itemsLength={items.length}
                        tonnage={tonnage}
                        source="details"
                    />
                    <ContentContainer>
                        <UploadItem title="Upload lorry receipt (optional)" field="lorry_receipt" />
                        <UploadItem title="Upload weight slip (optional)" field="weight_slip" />
                        <UploadItem title="Upload quality cert. (optional)" field="po_quality_certificate" />
                    </ContentContainer>

                    <WhiteWrapper className="po-bottom" style={{ position: 'absolute', bottom: 0, width: '100%', left: 0 }}>
                        <Button
                            className="fs-18 text-white fw-600"
                            onClick={handleSubmit}
                            disabled={uploadSupportingDocsStatus === STATUS_BEGIN || showLoader}
                            style={{
                                height: '52px',
                                borderRadius: '14px',
                                background: urls.lorry_receipt || urls.weight_slip || urls.po_quality_certificate ? "#ef4f5f" : "#D1D5DB",
                                boxShadow: urls.lorry_receipt || urls.weight_slip || urls.po_quality_certificate ? "0px 4px 12px rgba(239, 79, 95, 0.2)" : "none"
                            }}
                        >
                            Upload Documents
                        </Button>
                    </WhiteWrapper>
                </>
            )}
            {showToast && <ShowToast errorMsg={toastMsg} />}
            <SushiModal visible={showSuccessPopup} isCentered>
                <FlexColumn className="align-items-center p-5">
                    <img src={"/logos/tick_logo.svg"} height="80px" width="80px" />
                    <span className="fs-22 fw-700 my-4 text-center">Supporting documents uploaded successfully.</span>
                    <span
                        className="fs-18 fw-500 text-red-500"
                        onClick={handleSuccessPopupClose}
                        style={{ cursor: 'pointer' }}
                    >
                        Okay
                    </span>
                </FlexColumn>
            </SushiModal>
        </Wrapper>
    );
};

export default UploadSupportingDocs;
